import {
    IgnitionErrorPayload,
    IgnitionFrame,
} from '@common/http/ignition-error-dialog/ignition-error-payload';
import {
    IgnitionFilePath,
    IgnitionStackTrace,
} from '@common/http/ignition-error-dialog/ignition-stack-trace';
import { highlightCode } from '@common/text-editor/highlight/highlight-code';
import { Button } from '@ui/buttons/button';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import { ErrorIcon } from '@ui/icons/material/Error';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  error: IgnitionErrorPayload;
}
export function IgnitionErrorDialog({error}: Props) {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    for (const frame of error.trace) {
      if (!('vendorGroup' in frame)) {
        return frame.flatIndex;
      }
    }
    return 0;
  });
  const selectedFrame = useMemo(() => {
    for (const frame of error.trace) {
      if ('vendorGroup' in frame) {
        for (const vendorFrame of frame.items) {
          if (vendorFrame.flatIndex === selectedIndex) {
            return vendorFrame;
          }
        }
      } else if (frame.flatIndex === selectedIndex) {
        return frame;
      }
    }
  }, [error, selectedIndex]);

  return (
    <Dialog size="fullscreen">
      <DialogHeader
        showDivider
        leftAdornment={<ErrorIcon />}
        color="text-destructive"
        actions={<DownloadButton />}
      >
        <Trans message="An error occured" />
      </DialogHeader>
      <DialogBody padding="p-0 stack">
        <div className="sticky top-0 z-10 border-b bg p-6">
          <Chip className="w-max" radius="rounded-panel">
            {error.exception}
          </Chip>
          <div className="mt-4 line-clamp-2 text-lg leading-snug font-semibold">
            {error.message}
          </div>
        </div>
        <div className="flex items-stretch gap-2.5">
          <IgnitionStackTrace
            trace={error.trace}
            onSelectedIndexChange={setSelectedIndex}
            selectedIndex={selectedIndex}
            totalVendorGroups={error.totalVendorGroups}
          />
          {selectedFrame && <CodeSnippet frame={selectedFrame} />}
        </div>
      </DialogBody>
    </Dialog>
  );
}

interface CodeSnippetProps {
  frame: IgnitionFrame;
}
function CodeSnippet({frame}: CodeSnippetProps) {
  const codeRef = useRef<HTMLPreElement>(null!);

  const lineNumbers = Object.keys(frame.codeSnippet).map(Number);
  const highlightedIndex = lineNumbers.indexOf(frame.lineNumber);
  const lines = Object.values(frame.codeSnippet);

  return (
    <div className="sticky top-30 flex-auto">
      <div className="px-7.5 py-4 text-right text-muted-foreground">
        <IgnitionFilePath frame={frame} />
      </div>
      <div className="flex">
        <div className="mr-2 text-right select-none">
          {lineNumbers.map((lineNumber, index) => (
            <div
              className={clsx(
                'px-2 font-mono leading-loose text-muted-foreground',
                index == highlightedIndex && 'bg-destructive/30',
              )}
              key={index}
            >
              {lineNumber}
            </div>
          ))}
        </div>
        <div className="compact-scrollbar grow overflow-x-auto">
          <pre>
            <code className="language-php" ref={codeRef}>
              {lines.map((line, index) => (
                <CodeSnippetLine
                  isHighlighted={highlightedIndex === index}
                  key={`${frame.path}.${index}`}
                  line={line}
                />
              ))}
            </code>
          </pre>
        </div>
      </div>
    </div>
  );
}

interface CodeSnippetLineProps {
  line: string;
  isHighlighted: boolean;
}
const CodeSnippetLine = memo(({line, isHighlighted}: CodeSnippetLineProps) => {
  const ref = useRef<HTMLSpanElement>(null!);
  useEffect(() => {
    const el = ref.current;
    highlightCode(el, 'light');
    return () => {
      delete el.dataset.highlighted;
    };
  }, []);

  return (
    <span
      className={clsx(
        'block leading-loose',
        isHighlighted && 'bg-destructive/20',
      )}
    >
      <span className="language-php" ref={ref}>
        {line + '\n'}
      </span>
    </span>
  );
});

function DownloadButton() {
  return (
    <Button
      variant="outline"
      className="text-foreground"
      elementType="a"
      download
      href="api/v1/logs/error/download-latest"
      size="2xs"
    >
      <Trans message="Download log" />
    </Button>
  );
}
